import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './QRCodeGenerator.css';

function QRCodeGenerator() {
  const [text, setText] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [size, setSize] = useState(256);
  const [errorLevel, setErrorLevel] = useState('M');
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef(null);

  const generateQRCode = async () => {
    if (!text.trim()) return;

    setIsGenerating(true);
    try {
      const canvas = canvasRef.current;
      const options = {
        width: size,
        margin: 2,
        errorCorrectionLevel: errorLevel,
        color: {
          dark: foregroundColor,
          light: backgroundColor
        }
      };

      await QRCode.toCanvas(canvas, text, options);
      const dataUrl = canvas.toDataURL('image/png');
      setQrCodeUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.download = 'qrcode.png';
    link.href = qrCodeUrl;
    link.click();
  };

  const clearQRCode = () => {
    setText('');
    setQrCodeUrl('');
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const quickTemplates = [
    { name: 'Website URL', value: 'https://example.com' },
    { name: 'Email', value: 'mailto:someone@example.com' },
    { name: 'Phone', value: 'tel:+1234567890' },
    { name: 'SMS', value: 'sms:+1234567890?body=Hello' },
    { name: 'WiFi', value: 'WIFI:T:WPA;S:NetworkName;P:Password;;' },
    { name: 'Contact Card', value: 'BEGIN:VCARD\nVERSION:3.0\nFN:John Doe\nTEL:+1234567890\nEMAIL:john@example.com\nEND:VCARD' }
  ];

  useEffect(() => {
    if (text.trim()) {
      generateQRCode();
    }
  }, [text, size, errorLevel, foregroundColor, backgroundColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "QR Code Generator - Free QR Code Creator",
    "description": "Free QR code generator for URLs, text, contact information, WiFi credentials, and more. Customize size, colors, and error correction. Download as PNG.",
    "url": "https://infi-tools.com/qr-generator",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Generate QR codes for URLs, text, contact info",
      "WiFi QR code generator",
      "Customizable size and colors",
      "Error correction levels",
      "Download as PNG",
      "Quick templates",
      "Real-time preview"
    ]
  };

  return (
    <div className="qr-generator-page">
      <SEO 
        title="QR Code Generator - Free QR Code Creator with Custom Options"
        description="Generate free QR codes for URLs, text, contact information, WiFi credentials, and more. Customize size, colors, and error correction. Download high-quality PNG images."
        keywords="QR code generator, free QR code, QR code creator, WiFi QR code, contact QR code, URL QR code, QR code maker, custom QR code, download QR code"
        canonicalUrl="https://infi-tools.com/qr-generator"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>QR Code Generator</h1>
          <p className="page-description">
            Generate QR codes for URLs, text, contact information, WiFi credentials, and more.
          </p>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="5934782484" style={{ marginBottom: '20px' }} />

        <div className="qr-generator-container">
          <div className="input-section">
            <div className="input-group">
              <label htmlFor="qr-text">Enter text or data:</label>
              <textarea
                id="qr-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter URL, text, or any data you want to encode..."
                rows="4"
              />
            </div>

            <div className="quick-templates">
              <h3>Quick Templates:</h3>
              <div className="template-buttons">
                {quickTemplates.map((template, index) => (
                  <button
                    key={index}
                    className="template-btn"
                    onClick={() => setText(template.value)}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="settings-grid">
              <div className="setting-group">
                <label htmlFor="size">Size:</label>
                <select
                  id="size"
                  value={size}
                  onChange={(e) => setSize(parseInt(e.target.value))}
                >
                  <option value="128">128x128</option>
                  <option value="256">256x256</option>
                  <option value="384">384x384</option>
                  <option value="512">512x512</option>
                </select>
              </div>

              <div className="setting-group">
                <label htmlFor="error-level">Error Correction:</label>
                <select
                  id="error-level"
                  value={errorLevel}
                  onChange={(e) => setErrorLevel(e.target.value)}
                >
                  <option value="L">Low (~7%)</option>
                  <option value="M">Medium (~15%)</option>
                  <option value="Q">Quartile (~25%)</option>
                  <option value="H">High (~30%)</option>
                </select>
              </div>

              <div className="setting-group">
                <label htmlFor="fg-color">Foreground Color:</label>
                <div className="color-input">
                  <input
                    type="color"
                    id="fg-color"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    className="color-text"
                  />
                </div>
              </div>

              <div className="setting-group">
                <label htmlFor="bg-color">Background Color:</label>
                <div className="color-input">
                  <input
                    type="color"
                    id="bg-color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="color-text"
                  />
                </div>
              </div>
            </div>

            <div className="action-buttons">
              <button
                className="btn generate-btn"
                onClick={generateQRCode}
                disabled={!text.trim() || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate QR Code'}
              </button>
              <button className="btn clear-btn" onClick={clearQRCode}>
                Clear
              </button>
            </div>
          </div>

          <div className="output-section">
            <div className="qr-preview">
              <h3>Preview:</h3>
              <div className="qr-canvas-container">
                <canvas
                  ref={canvasRef}
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '8px'
                  }}
                />
                {!qrCodeUrl && (
                  <div className="qr-placeholder">
                    <p>Enter text above to generate QR code</p>
                  </div>
                )}
              </div>
            </div>

            {qrCodeUrl && (
              <div className="download-section">
                <button className="btn download-btn" onClick={downloadQRCode}>
                  📥 Download PNG
                </button>
                <p className="download-info">
                  Right-click on the QR code to save or copy the image
                </p>
              </div>
            )}

            <div className="qr-info">
              <h4>QR Code Information:</h4>
              <ul>
                <li><strong>Error Correction:</strong> Higher levels allow the QR code to be read even if partially damaged</li>
                <li><strong>Size:</strong> Larger sizes provide better readability for complex data</li>
                <li><strong>Colors:</strong> Ensure good contrast between foreground and background</li>
                <li><strong>Usage:</strong> QR codes can store URLs, text, contact info, WiFi credentials, and more</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="2298789802" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default QRCodeGenerator;